import { observer } from 'mobx-react-lite'
import { Box, BoxProps, CardContent, Skeleton, useTheme } from '@mui/material'
import CardContainer from './CardContainer'
import AnimatedBox from './Motion/AnimatedBox'

export type SkeletonSize = 'XL' | 'L' | 'M' | 'S' | '-'

interface Props {
  layout: SkeletonSize[][]
  card?: boolean
  p?: BoxProps['p']
  sx?: BoxProps['sx']
  staticWidth?: string // If SkeletonView is in a row of items, a width % will not work. Use this prop to set a static width.
  direction?: 'row' | 'column'
}

const SkeletonView = observer(({ layout, card, p, sx, staticWidth, direction = 'column' }: Props) => {
  const theme = useTheme()

  const renderSkeleton = (index: number, size: SkeletonSize) => {
    const skeletonSizes = {
      XL: { width: '100%', height: theme.spacing(6) },
      L: { width: '60%', height: theme.spacing(5) },
      M: { width: '60%', height: theme.spacing(4) },
      S: { width: '40%', height: theme.spacing(3) },
    }

    const key = `skeleton-${index}-${size}`

    if (size === '-') {
      return <Box sx={{ width: theme.spacing(1) }} key={key} />
    }

    return (
      <Skeleton
        key={key}
        sx={{
          ...sx,
          bgcolor: 'secondary.dark',
          ...skeletonSizes[size],
          ...(staticWidth && { width: staticWidth }),
        }}
        animation='wave'
        variant='rectangular'
      />
    )
  }

  const animationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.5, delay: 0.1 },
  }

  function skeletonKey(i: number, pattern: SkeletonSize[]) {
    const key = 'skeleton-' + i + '-' + pattern.flat().join('-')
    return key
  }

  if (card) {
    return (
      <AnimatedBox sx={{ p, gap: 3, display: 'flex', flexDirection: direction }} {...animationProps}>
        {layout.map((pattern, i) => {
          return (
            <CardContainer key={skeletonKey(i, pattern)}>
              <CardContent>
                <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
                  {pattern.map((size, index) => renderSkeleton(i + index, size))}
                </Box>
              </CardContent>
            </CardContainer>
          )
        })}
      </AnimatedBox>
    )
  }

  return <AnimatedBox sx={{ p, gap: 3, display: 'flex', flexDirection: 'column' }} {...animationProps}>
    {layout.map((pattern, i) => {
      return <Box key={skeletonKey(i, pattern)} sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
        {pattern.map((size, index) => renderSkeleton(index, size))}
      </Box>
    })}
  </AnimatedBox>
})

export default SkeletonView
